import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import { Close, ExpandMore } from "@mui/icons-material";

/**
 * Same review layout as parent Finished Test → view attempt (incorrect only).
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {boolean} props.loading
 * @param {string} props.error
 * @param {{ incorrect_questions?: object[], incorrect_count?: number } | null} props.viewData
 * @param {string} [props.contextSubtitle] — e.g. school / subject / topic line
 */
const ParentWrongQuestionsReviewDialog = ({ open, onClose, loading, error, viewData, contextSubtitle }) => {
  const [reviewQuestionIndex, setReviewQuestionIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setReviewQuestionIndex(0);
    }
  }, [open, viewData]);

  const incorrectList = viewData?.incorrect_questions;
  const reviewQuestion = useMemo(() => {
    if (!Array.isArray(incorrectList) || incorrectList.length === 0) return null;
    return incorrectList[Math.min(reviewQuestionIndex, incorrectList.length - 1)];
  }, [incorrectList, reviewQuestionIndex]);

  const reviewTotal = Array.isArray(incorrectList) ? incorrectList.length : 0;
  const reviewHowToSteps =
    reviewQuestion && Array.isArray(reviewQuestion.how_to_find_answer_steps)
      ? reviewQuestion.how_to_find_answer_steps.filter(Boolean)
      : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          pb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {!loading && !error && viewData && viewData.incorrect_count > 0
              ? `Question - Q${reviewQuestionIndex + 1}`
              : "Review wrong answers"}
          </Typography>
          {contextSubtitle ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {contextSubtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && <Typography color="error">{error}</Typography>}
        {!loading && !error && viewData && (
          <>
            {viewData.incorrect_count === 0 ? (
              <Typography>
                There are no incorrect answers in the latest attempts for this topic — every question the student
                answered was marked correct, or there were no attempts.
              </Typography>
            ) : (
              reviewQuestion && (
                <Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      mb: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: reviewQuestion.options?.length ? 2 : 0, lineHeight: 1.6 }}>
                      {reviewQuestion.question_text || "—"}
                    </Typography>
                    {Array.isArray(reviewQuestion.options) && reviewQuestion.options.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        {reviewQuestion.options.map((opt) => (
                          <Box
                            key={opt.option_id ?? opt.letter}
                            sx={{
                              textAlign: "center",
                              minWidth: 72,
                              flex: "1 1 72px",
                              maxWidth: 160,
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 0.5 }}>
                              {opt.letter}
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                              {opt.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>

                  <Accordion
                    defaultExpanded
                    disableGutters
                    elevation={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px !important",
                      overflow: "hidden",
                      mb: 1.5,
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: "common.white", fontSize: 20 }} />}
                      sx={{
                        bgcolor: "#1565c0",
                        color: "common.white",
                        minHeight: 44,
                        "& .MuiAccordionSummary-content": { my: 1 },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Answer
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 2, bgcolor: "background.paper" }}>
                      <Typography
                        align="center"
                        color="primary"
                        fontWeight={600}
                        variant="body2"
                        sx={{ fontSize: "0.8125rem" }}
                      >
                        Correct Answer:{" "}
                        {reviewQuestion.correct_letters?.length
                          ? reviewQuestion.correct_letters.join(", ")
                          : reviewQuestion.correct_answer || "—"}
                      </Typography>
                      <Typography
                        align="center"
                        color="primary"
                        fontWeight={600}
                        variant="body2"
                        sx={{ fontSize: "0.8125rem", mt: 1.25 }}
                      >
                        Chosen Answer:{" "}
                        {reviewQuestion.chosen_letter != null && reviewQuestion.chosen_letter !== ""
                          ? reviewQuestion.chosen_letter
                          : reviewQuestion.student_answer && reviewQuestion.student_answer !== "—"
                            ? reviewQuestion.student_answer
                            : "—"}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    defaultExpanded
                    disableGutters
                    elevation={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px !important",
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: "common.white", fontSize: 20 }} />}
                      sx={{
                        bgcolor: "#1565c0",
                        color: "common.white",
                        minHeight: 44,
                        "& .MuiAccordionSummary-content": { my: 1 },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Explanation
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 2, bgcolor: "background.paper" }}>
                      {reviewQuestion.explanation && String(reviewQuestion.explanation).trim() ? (
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{ fontSize: "0.8125rem", lineHeight: 1.65, whiteSpace: "pre-wrap" }}
                        >
                          {String(reviewQuestion.explanation).trim()}
                        </Typography>
                      ) : reviewHowToSteps.length > 0 ? (
                        <Box
                          component="ol"
                          sx={{
                            m: 0,
                            pl: 2.25,
                            color: "primary.main",
                            "& li": { pl: 0.5 },
                          }}
                        >
                          {reviewHowToSteps.map((step, idx) => (
                            <Typography
                              key={idx}
                              component="li"
                              variant="body2"
                              color="primary"
                              sx={{
                                fontSize: "0.8125rem",
                                lineHeight: 1.65,
                                whiteSpace: "pre-wrap",
                                pb: idx < reviewHowToSteps.length - 1 ? 0.75 : 0,
                              }}
                            >
                              {step}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                          —
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )
            )}
          </>
        )}
      </DialogContent>
      {!loading && !error && viewData && viewData.incorrect_count > 0 && (
        <DialogActions
          sx={{
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="contained"
            disabled={reviewQuestionIndex <= 0}
            onClick={() => setReviewQuestionIndex((i) => Math.max(0, i - 1))}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#90a4ae",
              color: "#fff",
              "&:hover": { bgcolor: "#78909c" },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            disabled={reviewQuestionIndex >= reviewTotal - 1}
            onClick={() => setReviewQuestionIndex((i) => Math.min(reviewTotal - 1, i + 1))}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#1565c0",
              "&:hover": { bgcolor: "#0d47a1" },
            }}
          >
            Next
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ParentWrongQuestionsReviewDialog;
